/**
 * editor2/modals/JsonDataModal.tsx
 * json_data.data のキー・値エディタ
 * Reserved fields (days, nights, short, lang) は読み取り専用
 * カスタムフィールドの追加/削除サブダイアログ付き
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const DEFAULT_RESERVED_FIELDS = ['days', 'nights', 'short', 'lang'];
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

interface JsonDataModalProps {
  open: boolean;
  jsonData: any;
  onSave: (newJsonData: any) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** 予約用デフォルトを上書きするとき（例: 車両用には ['label', 'mark', 'bg_color', 'text_color']） */
  reservedFields?: string[];
  /** ダイアログタイトル（既定: "その他データ設定"） */
  title?: string;
}

export default function JsonDataModal({
  open,
  jsonData,
  onSave,
  onCancel,
  disabled,
  reservedFields,
  title,
}: JsonDataModalProps) {
  const RESERVED_FIELDS = React.useMemo(
    () => new Set(reservedFields ?? DEFAULT_RESERVED_FIELDS),
    [reservedFields],
  );
  // Local copy of json_data for editing
  const [localData, setLocalData] = useState<any>(null);
  const [customFieldOpen, setCustomFieldOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  // Sync local state when modal opens
  React.useEffect(() => {
    if (open && jsonData) {
      // Deep clone
      setLocalData(JSON.parse(JSON.stringify(jsonData)));
    }
  }, [open, jsonData]);

  const currentData = localData || { data: {}, types: {} };
  const dataEntries = currentData.data ? Object.keys(currentData.data) : [];

  const handleFieldChange = (key: string, value: string) => {
    const updated = { ...currentData };
    updated.data = { ...updated.data, [key]: value };
    setLocalData(updated);
  };

  const handleDeleteField = (key: string) => {
    if (RESERVED_FIELDS.has(key)) return;
    if (!window.confirm(`「${key}」を削除してもよろしいですか？`)) return;

    const updated = { ...currentData };
    updated.data = { ...updated.data };
    updated.types = { ...updated.types };
    delete updated.data[key];
    delete updated.types[key];
    setLocalData(updated);
  };

  const handleAddCustomField = () => {
    const fieldName = newFieldName.trim();
    if (!fieldName) return;

    if (currentData.data && Object.prototype.hasOwnProperty.call(currentData.data, fieldName)) {
      window.alert('この項目名は既に存在します。');
      return;
    }

    const updated = { ...currentData };
    updated.data = { ...updated.data, [fieldName]: '' };
    updated.types = { ...(updated.types || {}), [fieldName]: newFieldType };
    setLocalData(updated);
    setNewFieldName('');
    setNewFieldType('text');
    setCustomFieldOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 12, padding: 8 } }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title ?? 'その他データ設定'}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setCustomFieldOpen(true)}
            size="small"
            disabled={disabled}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            カスタムデータを追加
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            {dataEntries.map((key) => {
              const fieldType = (currentData.types && currentData.types[key]) || 'text';
              const isPreset = RESERVED_FIELDS.has(key);
              return (
                <Box key={key}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isPreset ? 800 : 600,
                        color: isPreset ? '#0f172a' : '#64748b',
                      }}
                    >
                      {key} {fieldType === 'color' && '(HEX)'}
                    </Typography>
                    {!isPreset && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteField(key)}
                        disabled={disabled}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                  {fieldType === 'textarea' ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      value={currentData.data[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      disabled={disabled}
                      placeholder={`${key}を入力`}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : fieldType === 'color' ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={currentData.data[key] || ''}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        disabled={disabled}
                        placeholder="#000000"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <input
                        type="color"
                        value={
                          HEX_PATTERN.test(currentData.data[key])
                            ? currentData.data[key]
                            : '#ffffff'
                        }
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        disabled={disabled}
                        style={{
                          width: 42,
                          height: 40,
                          padding: 0,
                          border: '1px solid #cbd5e1',
                          borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type={fieldType === 'number' ? 'number' : 'text'}
                      value={currentData.data[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      disabled={disabled}
                      placeholder={`${key}を入力`}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: '#cbd5e1',
              color: '#475569',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={() => onSave(localData)}
            variant="contained"
            disableElevation
            sx={{ borderRadius: 2, px: 4, fontWeight: 600, textTransform: 'none' }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* カスタムフィールド追加サブダイアログ */}
      <Dialog
        open={customFieldOpen}
        onClose={() => setCustomFieldOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>カスタム項目を追加</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}
              >
                カスタム名
              </Typography>
              <TextField
                autoFocus
                fullWidth
                variant="outlined"
                size="small"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="項目名を入力 (例: テスト)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}
              >
                データタイプ
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="text">テキスト</MenuItem>
                  <MenuItem value="textarea">テキストエリア</MenuItem>
                  <MenuItem value="number">数値</MenuItem>
                  <MenuItem value="color">カラーパレット</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCustomFieldOpen(false)} sx={{ textTransform: 'none' }}>
            キャンセル
          </Button>
          <Button
            onClick={handleAddCustomField}
            variant="contained"
            disableElevation
            disabled={!newFieldName.trim()}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
          >
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
